/* Yarn and waste sales — the two registers, ex-mill, the month in focus.
 *
 * One screen for both, as the design has it: the picker swaps register and
 * the wording follows (counts become waste types, and the register's own
 * column headings change with it). The design was drawn when the waste
 * register had not been loaded and so showed quantities out of waste stock
 * with a note; the register is loaded now, so the waste side reads it the
 * same way as yarn and the note only appears when it is genuinely absent.
 */

import type { MSales, MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MRow } from '../MobileTable'
import {
  Bars, CR, Head, IN, KpiGrid, LK, NoteBox, SG, Seg, TN, kpi, type MBar,
} from '../parts'

export type SalesTab = 'yarn' | 'waste'

export function Sales({ data, tab, onTab }: {
  data: MobileSummary
  tab: SalesTab
  onTab: (t: SalesTab) => void
}) {
  const reg: MSales = tab === 'yarn' ? data.sales : data.wasteSales
  const seg = (
    <Seg value={tab} onChange={onTab}
      options={[{ v: 'yarn', l: 'Yarn' }, { v: 'waste', l: 'Waste' }]} />
  )

  if (reg.empty || !reg.months.length) {
    return (
      <>
        {seg}
        <NoteBox>
          The {tab} sales register hasn't been loaded yet. Upload it on the web
          app's Data loads page and this screen fills in.
        </NoteBox>
      </>
    )
  }

  const months = reg.months
  const focus = months.find((m) => m.mk === reg.mk) ?? months[months.length - 1]
  const prevIndex = months.indexOf(focus) - 1
  const prev = prevIndex >= 0 ? months[prevIndex] : null
  const short = reg.label.slice(0, 3)

  const realisation = focus.kg ? focus.v / focus.kg : null
  const prevReal = prev && prev.kg ? prev.v / prev.kg : null

  const maxV = Math.max(1, ...months.map((m) => m.v))
  const bars: MBar[] = months.map((m, i) => ({
    h: m.v / maxV * 86,
    c: i === months.length - 1 ? 'var(--c-ink)' : 'var(--c-bar1)',
    top: (m.v / 1e7).toFixed(1),
    lab: m.lab,
  }))

  const maxK = Math.max(1, ...reg.byCount.map((c) => c.kg))
  const countRows: MRow[] = reg.byCount.map((c) => ({
    cells: [
      { v: c.c },
      { v: IN(c.kg), bar: c.kg / maxK * 100, s: c.kg },
      { v: LK(c.v), s: c.v },
      { v: c.avg.toFixed(1) },
      { v: c.lo == null ? '–' : c.lo === c.hi ? String(c.lo) : `${c.lo}–${c.hi}`,
        s: c.lo ?? 0 },
      { v: String(c.n) },
      { v: IN(c.bags) },
    ],
  }))

  const invoiceRows: MRow[] = reg.recent.map((r) => ({
    cells: [
      { v: r.no.split('/').pop() ?? r.no, sub: r.cust || undefined },
      { v: r.d, s: parseInt(r.d, 10) },
      { v: r.c },
      { v: IN(r.kg) },
      { v: r.p == null ? '–' : String(r.p), s: r.p ?? 0 },
      { v: LK(r.v), s: r.v },
    ],
  }))

  const what = tab === 'yarn' ? 'Count' : 'Type'

  return (
    <>
      {seg}
      <div className="m-label" style={{ padding: '12px 0 2px' }}>
        {tab === 'yarn' ? 'Yarn' : 'Waste'} sales register · {reg.label} in focus
      </div>

      <KpiGrid style={{ marginTop: 16 }} items={[
        kpi(`Sales · ${short}`, CR(focus.v), 'ex-mill, before GST'),
        kpi(`Quantity · ${short}`, TN(focus.kg), `${focus.n} invoices`),
        kpi('Avg realisation', realisation == null ? '–' : `₹${realisation.toFixed(1)}`,
          prevReal != null && realisation != null
            ? `per kg · ${SG(realisation - prevReal, (x) => x.toFixed(1))} vs ${prev!.lab}`
            : 'per kg'),
        kpi('FY to date', CR(months.reduce((s, m) => s + m.v, 0)),
          TN(months.reduce((s, m) => s + m.kg, 0))),
      ]} />

      <Head title="Sales by month" aside="₹ Cr, ex-mill" />
      <div className="m-aside" style={{ marginTop: -6, marginBottom: 12 }}>
        Financial year to date
      </div>
      <Bars bars={bars} gap={12} />

      <Head title={`By ${what.toLowerCase()} · ${short}`} aside={`${reg.byCount.length}`} />
      <MobileTable cols={[
        { l: what, w: 96 }, { l: 'Kg', w: 116 }, { l: 'Value', w: 78 },
        { l: '₹ / kg', w: 64 }, { l: 'Range', w: 86 }, { l: 'Inv', w: 44 },
        { l: 'Bags', w: 58 },
      ]} rows={countRows} />

      <Head title="Latest invoices" aside={`${reg.recent.length} shown`} />
      <MobileTable cols={[
        { l: 'Invoice', w: 150 }, { l: 'Date', w: 62 }, { l: what, w: 80, al: 'left' },
        { l: 'Kg', w: 70 }, { l: '₹ / kg', w: 60 }, { l: 'Value', w: 76 },
      ]} rows={invoiceRows} />
    </>
  )
}
